import { Component, OnInit } from '@angular/core';
import { DataProcessContainerService } from '../../../Services/DataProcess/dataprocessContainer.service';
import { DataProcessContainer } from '../../../Model/dataProcess/dataprocesscontainer';
import { LoginService } from '../../../Services/login.service';
import { RowInsertedEvent, RowRemovedEvent, RowUpdatedEvent, ToolbarPreparingEvent } from 'devextreme/ui/data_grid';
import 'devextreme/ui/html_editor';
import { ClickEvent } from 'devextreme/ui/button';
import { WawiList } from 'app/Model/wawi/WawiList';
import { WawiListService } from 'app/Services/wawiList.service';
import { confirm } from 'devextreme/ui/dialog';
import { lastValueFrom } from 'rxjs';
import { TemplateItemTemplate } from 'app/Model/Catalog/TemplateItemTemplate';
import { UpdateTemplateService } from 'app/Services/updateTemplate.service';
import { ExportParameter } from 'app/Model/exportParameter/ExportParameter';
import { TemplateService } from 'app/Services/template.service';
import { ProductDataSource } from 'app/Model/dataProcess/productDataSource';
import { DataSourcesService } from 'app/Services/DataProcess/datasources.service';
import { DxSelectBoxTypes } from 'devextreme-angular/ui/select-box';
import { TranslateService } from '@ngx-translate/core';
import { BeforeSendEvent } from 'devextreme/ui/file_uploader';
import { OAuthService } from 'angular-oauth2-oidc';
import { DxButtonTypes } from 'devextreme-angular/ui/button';

@Component({
  selector: 'app-import-stage',
  templateUrl: './import-stage.component.html',
  styleUrls: ['./import-stage.component.css']
})
export class ImportStageComponent implements OnInit {
  private _prevConfig: ExportParameter;
  private _config: ExportParameter = new ExportParameter();
  private _allDataSources: ProductDataSource[] = [];

  editPopUpVisible = false;
  containers: DataProcessContainer[] = [];
  selectedContainer: DataProcessContainer;
  mappings: WawiList[];
  wawiMappingFields: TemplateItemTemplate[];
  fieldGroups: string[] = [];

  sourceTypes: DxSelectBoxTypes.Properties[];
  sourceDetails: ProductDataSource[] = [];
  uploadIcon: string = null;

  addMappingListEnabled = false;
  newMappingListOption: DxButtonTypes.Properties;
  mappingListName: string = null;
  selectedMappingList: WawiList = null;

  get config() {
    return this._config;
  }

  constructor(
    public dataProcessContainerService: DataProcessContainerService,
    public loginService: LoginService,
    private wawiListService: WawiListService,
    private updateTemplateService: UpdateTemplateService,
    private templateService: TemplateService,
    private dataSourcesService: DataSourcesService,
    private translate: TranslateService,
    private oAuthService: OAuthService
  ) {
    this.selectedContainer = new DataProcessContainer();
  }

  async ngOnInit() {
    this.containers = await this.dataProcessContainerService.getAll(this.loginService.currentCustomer.id);

    this.newMappingListOption = {
      text: this.translate.instant('Neue Liste hinzufügen'),
      onClick: () => {
        this.addMappingListEnabled = true;
      },
    };

    this.sourceTypes = [
      { text: this.translate.instant('Hersteller'), value: 'Manufacturer' },
      { text: this.translate.instant('Lieferant'), value: 'Supplier' },
      { text: this.translate.instant('ERP'), value: 'ERP' },
      { text: this.translate.instant('Sonstige'), value: 'Others' }
    ];

    // TODO: Auf Lieferanten pflegen
    this.wawiListService
      .getAllListsWithoutFields(this.loginService.currentCustomer.id)
      .subscribe((result) => (this.mappings = result.filter((x) => x.isMapping)));

    this._allDataSources = await this.dataSourcesService.getAll(this.loginService.currentCustomer.id);

    this.wawiMappingFields = await lastValueFrom(
      this.wawiListService.getWawiMappingFields(this.loginService.currentUser.customerId)
    );
    this.fieldGroups = this.updateTemplateService.GetFieldGroups();
    this.initConfig();
  }

  initConfig() {
    if (this._prevConfig != null && this._prevConfig?.id == this._config.id) return;

    this._prevConfig = new ExportParameter();
    this._config.containerId = this.selectedContainer.id;
    this._config.useTextQualifier = true;

    if (this._config.templateId != null) {
      this.templateService.getExportTemplates().then(() => {
        this.templateService.setCurrentExportTemplate(this._config.templateId);
      });
    } else {
      this.templateService.setCurrentExportTemplate(null);
    }
  }

  onToolbarPreparing(event: ToolbarPreparingEvent<DataProcessContainer>) {
    event.toolbarOptions.items.unshift({
      location: 'before',
      locateInMenu: 'auto',
      template: 'newDataProcessButtonTemplate'
    });
  }

  async onRowInserted(event: RowInsertedEvent) {
    let newModel = event.data;
    newModel.id = null;
    const data = await this.dataProcessContainerService.create(newModel, this.loginService.currentCustomer.id);
    this.containers.push(data);
  }

  async onRowUpdated(event: RowUpdatedEvent) {
    let newModel = event.data;
    await this.update(newModel);
  }

  async update(data: DataProcessContainer) {
    if (data.id) await this.dataProcessContainerService.update(data, this.loginService.currentCustomer.id);
    else await this.dataProcessContainerService.create(data, this.loginService.currentCustomer.id);
    const foundData = this.containers.find((x) => x.id === data.id);
    if (foundData) Object.assign(foundData, data);
    else this.containers.push(data);
    this.changeSourceType();
  }

  async onRowRemoved(event: RowRemovedEvent) {
    let key = event.key;
    await this.dataProcessContainerService.delete(key, this.loginService.currentCustomer.id);
    this.containers = this.containers.filter((x) => x.id !== key);
  }

  onBeforeSendUpload(event: BeforeSendEvent) {
    event.request.withCredentials = true;
    event.request.setRequestHeader('Authorization', 'Bearer ' + this.oAuthService.getAccessToken());
  }

  async onUploaded() {
    const data = await this.dataProcessContainerService.get(
      this.selectedContainer.id,
      this.loginService.currentCustomer.id
    );
    this.setSelectedContainer(data);
  }

  async clearIcon() {
    await this.dataProcessContainerService.deleteIcon(this.selectedContainer.id, this.loginService.currentCustomer.id);
    this.selectedContainer.icon = null;
    this.setSelectedContainer(this.selectedContainer);
  }

  async onAddMappingList(event: DxButtonTypes.ClickEvent) {
    const list: WawiList = new WawiList(null, this.mappingListName, null, null, null, null);
    list.containerId = this.selectedContainer.id;
    this.selectedMappingList = await lastValueFrom(this.wawiListService.create(list, this.loginService.currentCustomer.id));
    this.mappingListName = null;
  }

  async onCopyMappingList(event: DxButtonTypes.ClickEvent) {
    const list = {...this.selectedMappingList};
    list.id = null;
    list.containerId = this.selectedContainer.id;
    this.selectedMappingList = await lastValueFrom(this.wawiListService.create(list, this.loginService.currentCustomer.id));
    this.addMappingListEnabled = false;
  }

  async onReferenceMappingList(event: DxButtonTypes.ClickEvent) {
    this.selectedMappingList.containerId = this.selectedContainer.id;
    this.selectedMappingList = await lastValueFrom(this.wawiListService.update(this.selectedMappingList, this.loginService.currentCustomer.id));
    this.addMappingListEnabled = false;
  }

  async removeContainer(event: ClickEvent, data: DataProcessContainer) {
    const yes = await confirm('Soll der Container wirklich gelöscht werden?', 'Bestätigung');
    if (!yes) return;

    await this.dataProcessContainerService.delete(data.id, this.loginService.currentCustomer.id);
    this.containers = this.containers.filter((x) => x.id !== data.id);
  }

  async showAddContainer(event: ClickEvent) {
    const data = await this.dataProcessContainerService.create(
      new DataProcessContainer(),
      this.loginService.currentCustomer.id
    );
    this.toogglePopUp(event, data);
  }

  setSelectedContainer(data: DataProcessContainer) {
    this.selectedContainer = data;
    if (this.selectedContainer.icon) {
      this.uploadIcon = this.selectedContainer.icon + '?' + new Date().getTime();
    } else {
      this.uploadIcon = null;
    }
    this.changeSourceType();
  }

  toogglePopUp(event: ClickEvent, data: DataProcessContainer) {
    event.event.stopPropagation();
    this.setSelectedContainer(data);

    this.editPopUpVisible = !this.editPopUpVisible;
  }

  private changeSourceType() {
    if (this.selectedContainer.sourceType === 'Manufacturer' || this.selectedContainer.sourceType === 'Supplier') {
      this.sourceDetails = this._allDataSources.filter((x) => x.type == this.selectedContainer.sourceType);
      this.selectedContainer.sourceDetails =
        this.sourceDetails.find((x) => x.name === this.selectedContainer.sourceDetails)?.name || null;
    }
  }
}
